import React, { useState, createElement, Component } from 'react';
import { X, Copy, ExternalLink, MapPin, Check, Phone, Navigation, Receipt, Truck, Store, UtensilsCrossed, CreditCard, Banknote, Smartphone } from 'lucide-react';
import { cn } from '../../lib/utils';
interface OrderItem {
  id: string;
  item_name_snapshot: string;
  unit_price_snapshot: number;
  quantity: number;
  notes?: string | null;
  status: string;
  options?: {
    option_name: string;
    price_delta: number;
  }[];
}
interface KDSOrder {
  id: string;
  order_number: string;
  customer_name: string;
  customer_phone?: string | null;
  delivery_type: string;
  payment_method: string;
  delivery_address?: string | null;
  maps_url?: string | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  notes?: string | null;
  subtotal: number;
  delivery_fee: number;
  total: number;
  tracking_token?: string | null;
  ticket_image_url?: string | null;
  created_at: string;
  status: string;
  source?: string;
  items: OrderItem[];
}
interface KDSOrderDetailModalProps {
  order: KDSOrder | null;
  isOpen: boolean;
  onClose: () => void;
  currencySymbol?: string;
  restaurantName?: string;
}
function formatDeliveryType(type: string): string {
  switch (type) {
    case 'delivery':
      return 'Delivery';
    case 'pickup':
      return 'Para llevar';
    case 'dine_in':
      return 'Comer aquí';
    default:
      return type || 'N/A';
  }
}
function formatPaymentMethod(method: string): string {
  switch (method) {
    case 'sinpe':
      return 'SINPE Móvil';
    case 'cash':
      return 'Efectivo';
    case 'card':
      return 'Tarjeta';
    default:
      return method || 'N/A';
  }
}
function formatTime(dateString: string): string {
  const d = new Date(dateString);
  return d.toLocaleTimeString('es-CR', {
    hour: '2-digit',
    minute: '2-digit'
  });
}
function formatDate(dateString: string): string {
  const d = new Date(dateString);
  return d.toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
export function KDSOrderDetailModal({
  order,
  isOpen,
  onClose,
  currencySymbol = '₡',
  restaurantName = ''
}: KDSOrderDetailModalProps) {
  const [copied, setCopied] = useState(false);
  if (!isOpen || !order) return null;
  const cs = currencySymbol;
  const fmt = (n: number) => n.toLocaleString();
  const trackingUrl = order.tracking_token ? `${window.location.origin}/track/${order.tracking_token}` : null;
  const mapsUrl = order.maps_url || (order.delivery_lat && order.delivery_lng ? `https://www.google.com/maps?q=${order.delivery_lat},${order.delivery_lng}` : null);
  // ─── Build WhatsApp-identical plain text ───────────────────
  const buildPlainText = (): string => {
    let msg = '';
    msg += `🔥 *NUEVO PEDIDO${restaurantName ? ` — ${restaurantName}` : ''}*\n\n`;
    msg += `📦 *Pedido #${order.order_number}*\n`;
    msg += `👤 Cliente: ${order.customer_name}\n`;
    if (order.customer_phone) msg += `📞 Tel: ${order.customer_phone}\n`;
    msg += '\n';
    msg += `🛒 *PEDIDO:*\n\n`;
    order.items.forEach((item, idx) => {
      const optTotal = (item.options || []).reduce((s, o) => s + o.price_delta, 0);
      const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity;
      msg += `${idx + 1}. ${item.item_name_snapshot} (x${item.quantity}) — ${cs}${fmt(lineTotal)}`;
      if (item.options && item.options.length > 0) {
        for (const opt of item.options) {
          const delta = opt.price_delta > 0 ? ` +${cs}${fmt(opt.price_delta)}` : '';
          msg += `\n   ${opt.option_name}${delta}`;
        }
      }
      if (item.notes) {
        msg += `\n   📝 ${item.notes}`;
      }
      msg += '\n\n';
    });
    msg += `💰 Subtotal: ${cs}${fmt(order.subtotal)}\n`;
    if (order.delivery_fee > 0) {
      msg += `🚚 Envío: ${cs}${fmt(order.delivery_fee)}\n`;
    }
    msg += `✅ *TOTAL: ${cs}${fmt(order.total)}*\n\n`;
    msg += `🚚 Entrega: ${formatDeliveryType(order.delivery_type)}\n`;
    msg += `💳 Pago: ${formatPaymentMethod(order.payment_method)}\n`;
    if (order.delivery_address) {
      msg += `\n📍 *Dirección:*\n${order.delivery_address}\n`;
    }
    if (mapsUrl) {
      msg += `🗺️ ${mapsUrl}\n`;
    }
    msg += `\n📝 Notas: ${order.notes || 'Sin notas'}\n`;
    if (trackingUrl) {
      msg += `\n🔎 *Seguimiento:*\n${trackingUrl}\n`;
    }
    if (order.ticket_image_url) {
      // Use the short on-domain viewer URL — more reliable on WhatsApp Android
      // than raw Cloudinary links. Falls back to the direct URL if order_number
      // is unavailable.
      const ticketViewerUrl = order.order_number ? `${window.location.origin}/ticket/${encodeURIComponent(order.order_number)}` : order.ticket_image_url;
      msg += `\n🧾 *Ticket:*\n${ticketViewerUrl}\n`;
    }
    return msg;
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = buildPlainText();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  // ─── Render ────────────────────────────────────────────────
  return <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center" onClick={onClose} data-id="element-442">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-443" />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md max-h-[92vh] bg-[#111b21] border border-white/10 rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-444">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#1f2c34] border-b border-white/5 shrink-0" data-id="element-445">
          <div className="flex items-center gap-3" data-id="element-446">
            <div className="w-9 h-9 bg-emerald-600 rounded-full flex items-center justify-center" data-id="element-447">
              <Receipt className="w-4 h-4 text-white" data-id="element-448" />
            </div>
            <div data-id="element-449">
              <h2 className="text-sm font-bold text-white" data-id="element-450">
                Detalle del pedido
              </h2>
              <p className="text-[11px] text-white/40" data-id="element-451">
                {formatDate(order.created_at)} · {formatTime(order.created_at)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-id="element-452">
            <X className="w-4 h-4 text-white/60" data-id="element-453" />
          </button>
        </div>

        {/* WhatsApp-style message content */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#0b141a]" data-id="element-454">
          {/* ═══ Message bubble ═══ */}
          <div className="bg-[#005c4b] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm" data-id="element-455">
            {/* Title line */}
            <p className="text-[13px] font-bold text-white leading-snug" data-id="element-456">
              🔥 NUEVO PEDIDO{restaurantName ? ` — ${restaurantName}` : ''}
            </p>
          </div>

          {/* ═══ Order info bubble ═══ */}
          <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-1" data-id="element-457">
            <p className="text-[13px] text-white font-bold leading-snug" data-id="element-458">
              📦 Pedido #{order.order_number}
            </p>
            <p className="text-[13px] text-white/80 leading-snug" data-id="element-459">
              👤 Cliente: {order.customer_name}
            </p>
            {order.customer_phone && <p className="text-[13px] text-white/80 leading-snug" data-id="element-460">
                📞 Tel:{' '}
                <a href={`tel:${order.customer_phone}`} className="text-[#53bdeb] hover:underline" data-id="element-461">
                  {order.customer_phone}
                </a>
              </p>}
          </div>

          {/* ═══ Items bubble ═══ */}
          <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm" data-id="element-462">
            <p className="text-[13px] font-bold text-white leading-snug mb-2" data-id="element-463">
              🛒 PEDIDO:
            </p>
            <div className="space-y-2.5" data-id="element-464">
              {order.items.map((item, idx) => {
              const optTotal = (item.options || []).reduce((s, o) => s + o.price_delta, 0);
              const lineTotal = (item.unit_price_snapshot + optTotal) * item.quantity;
              return <div key={item.id || idx} className="text-[13px] leading-snug" data-id="element-465">
                    <p className="text-white" data-id="element-466">
                      {idx + 1}. {item.item_name_snapshot} (x{item.quantity}) —{' '}
                      <span className="text-white/70" data-id="element-467">
                        {cs}
                        {fmt(lineTotal)}
                      </span>
                    </p>
                    {item.options && item.options.length > 0 && <div className="pl-3.5 mt-0.5" data-id="element-468">
                        {item.options.map((opt, oi) => {
                    const delta = opt.price_delta > 0 ? ` +${cs}${fmt(opt.price_delta)}` : '';
                    return <p key={oi} className="text-white/50 text-[12px]" data-id="element-469">
                              {opt.option_name}
                              {delta}
                            </p>;
                  })}
                      </div>}
                    {item.notes && <p className="pl-3.5 text-[12px] text-amber-300 mt-0.5" data-id="element-470">
                        📝 {item.notes}
                      </p>}
                  </div>;
            })}
            </div>
          </div>

          {/* ═══ Totals bubble ═══ */}
          <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-0.5" data-id="element-471">
            <p className="text-[13px] text-white/80 leading-snug" data-id="element-472">
              💰 Subtotal: {cs}
              {fmt(order.subtotal)}
            </p>
            {order.delivery_fee > 0 && <p className="text-[13px] text-white/80 leading-snug" data-id="element-473">
                🚚 Envío: {cs}
                {fmt(order.delivery_fee)}
              </p>}
            <p className="text-[13px] text-white font-bold leading-snug pt-1" data-id="element-474">
              ✅ TOTAL: {cs}
              {fmt(order.total)}
            </p>
          </div>

          {/* ═══ Delivery & Payment bubble ═══ */}
          <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-0.5" data-id="element-475">
            <p className="text-[13px] text-white/80 leading-snug" data-id="element-476">
              🚚 Entrega: {formatDeliveryType(order.delivery_type)}
            </p>
            <p className="text-[13px] text-white/80 leading-snug" data-id="element-477">
              💳 Pago: {formatPaymentMethod(order.payment_method)}
            </p>
          </div>

          {/* ═══ Address bubble ═══ */}
          {(order.delivery_address || mapsUrl) && <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-1" data-id="element-478">
              {order.delivery_address && <>
                  <p className="text-[13px] text-white font-bold leading-snug" data-id="element-479">
                    📍 Dirección:
                  </p>
                  <p className="text-[13px] text-white/70 leading-snug" data-id="element-480">
                    {order.delivery_address}
                  </p>
                </>}
              {mapsUrl && <p className="text-[13px] leading-snug" data-id="element-481">
                  🗺️{' '}
                  <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-[#53bdeb] hover:underline break-all" data-id="element-482">
                    {mapsUrl}
                  </a>
                </p>}
            </div>}

          {/* ═══ Notes bubble ═══ */}
          <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm" data-id="element-483">
            <p className="text-[13px] text-white/80 leading-snug" data-id="element-484">
              📝 Notas: {order.notes || 'Sin notas'}
            </p>
          </div>

          {/* ═══ Tracking bubble ═══ */}
          {trackingUrl && <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-1" data-id="element-485">
              <p className="text-[13px] text-white font-bold leading-snug" data-id="element-486">
                🔎 Seguimiento:
              </p>
              <p className="text-[13px] leading-snug" data-id="element-487">
                <a href={trackingUrl} target="_blank" rel="noreferrer" className="text-[#53bdeb] hover:underline break-all" data-id="element-488">
                  {trackingUrl}
                </a>
              </p>
            </div>}

          {/* ═══ Ticket bubble ═══ */}
          {order.ticket_image_url && <div className="bg-[#1f2c34] rounded-lg rounded-tl-none px-3.5 py-2.5 max-w-[95%] shadow-sm space-y-1" data-id="element-489">
              <p className="text-[13px] text-white font-bold leading-snug" data-id="element-490">
                🧾 Ticket:
              </p>
              <p className="text-[13px] leading-snug" data-id="element-491">
                <a href={order.ticket_image_url} target="_blank" rel="noreferrer" className="text-[#53bdeb] hover:underline break-all" data-id="element-492">
                  {order.ticket_image_url}
                </a>
              </p>
            </div>}

          {/* Timestamp — right-aligned like WhatsApp */}
          <div className="flex justify-end pr-1" data-id="element-493">
            <span className="text-[10px] text-white/25" data-id="element-494">
              {formatTime(order.created_at)}
            </span>
          </div>
        </div>

        {/* ═══ Action buttons ═══ */}
        <div className="px-3 py-2.5 bg-[#1f2c34] border-t border-white/5 shrink-0 space-y-2" data-id="element-495">
          {/* Quick links row */}
          <div className="flex gap-2" data-id="element-496">
            {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-blue-500/15 border border-blue-500/20 rounded-lg text-[11px] font-bold text-blue-400 hover:bg-blue-500/25 transition-colors" data-id="element-497">
                <Navigation className="w-3.5 h-3.5" data-id="element-498" />
                Abrir mapa
              </a>}
            {trackingUrl && <a href={trackingUrl} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-emerald-500/15 border border-emerald-500/20 rounded-lg text-[11px] font-bold text-emerald-400 hover:bg-emerald-500/25 transition-colors" data-id="element-499">
                <ExternalLink className="w-3.5 h-3.5" data-id="element-500" />
                Seguimiento
              </a>}
            {order.ticket_image_url && <a href={order.ticket_image_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 h-9 bg-amber-500/15 border border-amber-500/20 rounded-lg text-[11px] font-bold text-amber-400 hover:bg-amber-500/25 transition-colors" data-id="element-501">
                <Receipt className="w-3.5 h-3.5" data-id="element-502" />
                Ver ticket
              </a>}
          </div>

          {/* Copy button */}
          <button onClick={handleCopy} className={cn('w-full h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all active:scale-95', copied ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/70 border border-white/10 hover:bg-white/10')} data-id="element-503">
            {copied ? <>
                <Check className="w-4 h-4" data-id="element-504" />
                Texto copiado
              </> : <>
                <Copy className="w-4 h-4" data-id="element-505" />
                Copiar texto del pedido
              </>}
          </button>
        </div>
      </div>
    </div>;
}